import { useEffect, useRef, useState } from "react";
import PlaceHolderImage from "../../assets/home/placeholder-image.webp";
import { usePropertyData } from "../../context/PropertyDataContext";
import { DateFormat } from "../common/utils/getTimeAgo";
import { Link } from "react-router-dom";
import { ArrowRight, Calendar, ChevronRight, Clock4, Newspaper } from "lucide-react";

const NewsEmptyState = () => (
  <div className="w-full md:col-span-2 lg:col-span-4 bg-white border-[1.5px] border-[#EAEAEA] rounded-2xl flex flex-col items-center justify-center text-center px-6 py-10 sm:px-10 sm:py-12 min-h-[260px] shadow-sm">
    <div
      className="w-16 h-16 sm:w-[72px] sm:h-[72px] rounded-full bg-[#F3F6F9] flex items-center justify-center mb-5 ring-1 ring-[#EAEAEA]"
      aria-hidden
    >
      <Newspaper className="text-[#097FE6]" size={36} strokeWidth={1.75} />
    </div>
    <h3 className="text-lg sm:text-xl font-semibold text-[#000929] mb-2 max-w-md">
      No articles yet
    </h3>
    <p className="text-sm sm:text-base text-gray-500 max-w-lg mb-6 leading-relaxed">
      There aren&apos;t any stories to show here right now. Visit the blog to see all property news and guides.
    </p>
    <Link to="/blog" className="w-full sm:w-auto">
      <button
        type="button"
        className="w-full sm:w-auto rounded-full px-6 sm:px-8 py-2.5 sm:py-3 font-medium text-sm sm:text-base bg-[#097FE6] text-white hover:opacity-95 transition-opacity"
      >
        See all articles
      </button>
    </Link>
  </div>
);

const NewsErrorState = ({ onRetry }) => (
  <div className="w-full md:col-span-2 lg:col-span-4 bg-white border-[1.5px] border-[#EAEAEA] rounded-2xl flex flex-col items-center justify-center text-center px-6 py-10 sm:px-10 sm:py-12 min-h-[260px] shadow-sm">
    <div
      className="w-16 h-16 sm:w-[72px] sm:h-[72px] rounded-full bg-[#FEF2F2] flex items-center justify-center mb-5 ring-1 ring-[#FECACA]"
      aria-hidden
    >
      <span className="text-2xl sm:text-3xl font-semibold text-[#DC2626]" aria-hidden>
        !
      </span>
    </div>
    <h3 className="text-lg sm:text-xl font-semibold text-[#000929] mb-2 max-w-md">
      Couldn&apos;t load articles
    </h3>
    <p className="text-sm sm:text-base text-gray-500 max-w-lg mb-6 leading-relaxed">
      Something went wrong while fetching the list. Check your connection and try again.
    </p>
    <button
      type="button"
      onClick={onRetry}
      className="w-full sm:w-auto rounded-full px-6 sm:px-8 py-2.5 sm:py-3 font-medium text-sm sm:text-base bg-[#097FE6] text-white hover:opacity-95 transition-opacity"
    >
      Try again
    </button>
  </div>
);

const LeastNewsProperty = () => {
  const { fetchBlogs, blogCache } = usePropertyData();
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      setFetchError(false);
      const result = await fetchBlogs();
      if (!cancelled) {
        setFetchError(!result?.ok);
        setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleRetry = async () => {
    setIsLoading(true);
    setFetchError(false);
    const result = await fetchBlogs({ force: true });
    setFetchError(!result?.ok);
    setIsLoading(false);
  };

  const newsData = Array.isArray(blogCache)
    ? blogCache.map((data) => ({
      id: data?._id,
      title: data?.title,
      image: data?.coverImage,
      readTime: data.createdAt,
      sponsor: data.sponsor,
    }))
    : [];

  const scrollRef = useRef(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);

  useEffect(() => {
    const handleScroll = () => {
      if (!scrollRef.current) return;
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;

      setShowLeftArrow(scrollLeft > 0);
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth);
    };

    const scrollContainer = scrollRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener("scroll", handleScroll);
      handleScroll(); // Run initially to set correct button states
    }

    return () => scrollContainer?.removeEventListener("scroll", handleScroll);
  }, []);

  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = 300;
      scrollRef.current.scrollBy({
        left: direction === "left" ? -scrollAmount : scrollAmount,
        behavior: "smooth",
      });
    }
  };

  const NewsSkeletonCard = () => (
    <div className="w-full md:w-[300px] bg-white rounded-lg animate-pulse">
      <div className="w-full h-32 bg-gray-300 rounded-t-lg" />
      <div className="py-3 px-2">
        <div className="h-4 bg-gray-300 rounded w-3/4 mb-2" />
        <div className="h-3 bg-gray-200 rounded w-1/2" />
      </div>
    </div>
  );

  return (
    <div className="container py-10 sm:py-14 lg:py-16 relative">
      <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4">
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-[#000929]">
          Latest Property News
        </h2>
        <Link to="/blog">
          <button className="text-[#097FE6] border-[2px] text-sm sm:text-base border-[#EAEAEA] rounded-full py-2 px-4 sm:px-5 flex justify-center items-center gap-1 sm:gap-2 whitespace-nowrap">
            <span>See All Article</span>
            <ChevronRight size={18} />
          </button>
        </Link>
      </div>
      <div className="relative">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {isLoading ? (
            <>
              <NewsSkeletonCard />
              <NewsSkeletonCard />
              <NewsSkeletonCard />
              <NewsSkeletonCard />
            </>
          ) : fetchError ? (
            <NewsErrorState onRetry={handleRetry} />
          ) : newsData.length === 0 ? (
            <NewsEmptyState />
          ) : (
            newsData.slice(0, 4).map((news, index) => (
              <Link
                to={`/blog/${news.id}`}
                key={news.id ?? `news-${index}`}
                className="w-full flex-1 group"
              >
                <div className="hover:shadow-md bg-white border border-[#EAEAEA] rounded-lg w-full">
                  <div className="overflow-hidden rounded-t-lg">
                    <img
                      src={news.image || PlaceHolderImage}
                      alt=""
                      className="w-full h-40 sm:h-44 object-cover transition-transform duration-500 ease-in-out hover:scale-110"
                    />
                  </div>
                  <div className="p-3 sm:p-4">
                    <div className="flex flex-wrap items-center gap-3 sm:gap-4 text-gray-500 text-xs sm:text-sm">
                      <div className="flex items-center gap-1">
                        <Calendar size={16} aria-hidden />
                        <span>{DateFormat(news.readTime)}</span>
                      </div>

                      <div className="flex items-center gap-1">
                        <Clock4 size={16} aria-hidden />
                        <span>5 min read</span>
                      </div>
                    </div>

                    <p className="mt-2 sm:mt-3 text-gray-700 text-sm leading-relaxed min-h-[48px] sm:min-h-[60px]">
                      {news.title}
                    </p>

                    <div className="mt-3 sm:mt-4">
                      <span className="flex font-medium items-center gap-1 text-[#097FE6] group-hover:gap-2 transition-all text-xs sm:text-sm pointer-events-none">
                        Read Article
                        <ArrowRight size={14} aria-hidden />
                      </span>
                    </div>
                  </div>
                </div>
              </Link>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default LeastNewsProperty;
